<template>
  <ValidationObserver ref="observer" slim>
    <div class="referral-info-tab">
      <student-identification-section
        v-model="model"
        :read-only="isReadonlyMode"
      />
      <disability-medical-info-section
        v-model="model.disabilityMedicalInfo"
        :read-only="isReadonlyMode"
      />
      <educational-history-section
        v-model="model.educationalHistoryInfo"
        :read-only="isReadonlyMode"
      />
      <additional-information-section
        v-model="model.additionalInformation"
        :read-only="isReadonlyMode"
      />
    </div>
  </ValidationObserver>
</template>

<script>
import AdditionalInformationSection from './components/AdditionalInformationSection.vue';
import DisabilityMedicalInfoSection from './components/DisabilityMedicalInfoSection.vue';
import EducationalHistorySection from './components/EducationalHistorySection.vue';
import StudentIdentificationSection from './components/StudentIdentificationSection.vue';

export default {
  name: 'ReferralInfoTab',
  components: {
    AdditionalInformationSection,
    DisabilityMedicalInfoSection,
    EducationalHistorySection,
    StudentIdentificationSection,
  },
  props: {
    value: {
      type: Object,
      default: () => null,
    },
    readOnly: Boolean,
    isReadOnly: Boolean,
    referralInfo: {
      type: Object,
      default: () => null,
    },
    validationItems: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    model: {
      get() {
        return this.value?.referralInfo || this.referralInfo || {};
      },
      set(value) {
        this.$emit('input', { referralInfo: value });
        this.$emit('update:model', { referralInfo: value });
      },
    },
    isReadonlyMode() {
      return this.readOnly || this.isReadOnly;
    },
  },
  methods: {
    async validate() {
      const formValid = await this.$refs.observer?.validate();
      const items = this.getPendingItems();
      const pendingItems = items.length
        ? items
        : this.getObserverPendingItems();

      return {
        tabId: 'ReferralInfo',
        title: 'Información del Referido',
        valid: Boolean(formValid) && pendingItems.length === 0,
        items: pendingItems,
      };
    },
    getObserverPendingItems() {
      const errors = this.$refs.observer?.errors || {};

      return Object.entries(errors)
        .filter(([, messages]) => messages?.length)
        .map(([id, messages]) => ({
          id,
          label: messages[0],
          selector: '.referral-info-tab',
        }));
    },
    getPendingItems() {
      const isEmpty = (value) =>
        value === null ||
        value === undefined ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '');
      const hasMinLength = (value, min) =>
        typeof value === 'string' && value.trim().length >= min;
      const add = (id, label, selector) => ({ id, label, selector });
      const info = this.model || {};
      const medical = info.disabilityMedicalInfo || {};
      const education = info.educationalHistoryInfo || {};
      const additional = info.additionalInformation || {};
      const items = [];

      if (isEmpty(info.referralDate)) {
        items.push(add('referralDate', 'Fecha de Referido.', '.date-field'));
      }

      if (isEmpty(medical.disabilityCategory?.id)) {
        items.push(
          add(
            'disabilityCategory',
            'Categoría de Discapacidad.',
            '.category-field'
          )
        );
      }

      if (!Array.isArray(medical.diagnoses) || medical.diagnoses.length === 0) {
        items.push(
          add(
            'diagnoses',
            'Debe añadir al menos un diagnóstico antes de continuar.',
            '.diagnosis-list'
          )
        );
      }

      if (isEmpty(medical.isReceivingTreatment)) {
        items.push(
          add(
            'isReceivingTreatment',
            'Indicar si recibe tratamiento.',
            '[name="receiving-treatment"]'
          )
        );
      }

      if (
        medical.isReceivingTreatment === true &&
        isEmpty(medical.treatmentDescription)
      ) {
        items.push(
          add(
            'treatmentDescription',
            'Descripción del tratamiento.',
            '.question-textarea'
          )
        );
      }

      if (isEmpty(medical.usesMedication)) {
        items.push(
          add(
            'usesMedication',
            'Indicar si utiliza medicamento.',
            '[name="uses-medication"]'
          )
        );
      }

      if (
        medical.usesMedication === true &&
        isEmpty(medical.medicationDescription)
      ) {
        items.push(
          add(
            'medicationDescription',
            'Descripción de medicamento y dosis.',
            '.question-textarea'
          )
        );
      }

      if (isEmpty(medical.walksIndependently)) {
        items.push(
          add(
            'walksIndependently',
            'Indicar si camina independientemente.',
            '[name="walks-independently"]'
          )
        );
      }

      if (isEmpty(medical.canTravelAlone)) {
        items.push(
          add(
            'canTravelAlone',
            'Indicar si puede viajar solo.',
            '[name="can-travel-alone"]'
          )
        );
      }

      if (isEmpty(medical.performsDailySelfCareActivities)) {
        items.push(
          add(
            'performsDailySelfCareActivities',
            'Indicar si realiza actividades diarias del cuidado propio.',
            '[name="daily-self-care"]'
          )
        );
      }

      if (isEmpty(education.isCurrentlyStudying)) {
        items.push(
          add(
            'isCurrentlyStudying',
            'Indicar si se encuentra estudiando actualmente.',
            '[name="currently-studying"]'
          )
        );
      }

      if (!education.completedGradeProgram?.id) {
        items.push(
          add(
            'completedGradeProgram',
            'Último grado o programa completado.',
            '.grade-field'
          )
        );
      }

      if (isEmpty(education.hasVocationalInterestEvaluationReport)) {
        items.push(
          add(
            'hasVocationalInterestEvaluationReport',
            'Indicar si cuenta con informe de apreciación o evaluación de intereses vocacionales.',
            '[name="vocational-interest-report"]'
          )
        );
      }

      if (
        education.hasVocationalInterestEvaluationReport === true &&
        !education.vocationalInterestEvaluationDocument &&
        !education.vocationalInterestEvaluationFile
      ) {
        items.push(
          add(
            'vocationalInterestEvaluationFile',
            'Documento del informe de apreciación o evaluación de intereses vocacionales.',
            '.document-row'
          )
        );
      }

      if (isEmpty(education.isWillingToCompleteVocationalGoal)) {
        items.push(
          add(
            'isWillingToCompleteVocationalGoal',
            'Indicar si demuestra disposición para completar un objetivo vocacional.',
            '[name="vocational-goal-willingness"]'
          )
        );
      }

      if (!hasMinLength(additional.referralReason, 20)) {
        items.push(
          add(
            'referralReason',
            'Razón del referido debe tener al menos 20 caracteres.',
            '.additional-information .question-textarea--spaced'
          )
        );
      }

      return items;
    },
  },
};
</script>

<style lang="scss" scoped>
.referral-info-tab {
  padding: 18px 8px 8px;
}

.tab-validation-warning {
  background: #fff7e8;
  border: 1px solid #d99025;
  border-radius: 4px;
  color: #8a5200;
  padding: 10px 12px;
}

.tab-validation-warning__message {
  align-items: center;
  display: flex;
  font-weight: 700;
  margin-bottom: 6px;
}

.tab-validation-warning__items {
  margin: 0;
  padding-left: 26px;
}

.tab-validation-warning__items li {
  margin: 2px 0;
}
</style>
